import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { FaUser, FaEnvelope, FaPhone, FaBriefcase, FaGraduationCap, FaCalendarAlt } from 'react-icons/fa';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 300px 1fr;
  }
`;

const ProfileImageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProfileImage = styled.div`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.background};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 3px solid ${({ theme }) => theme.colors.primary};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  svg {
    font-size: 5rem;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}30;
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.backgroundAlt};
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}30;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}30;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ErrorMessage = styled.div`
  color: #e53e3e;
  margin-top: 0.5rem;
  font-size: 0.875rem;
`;

const SuccessMessage = styled.div`
  color: #38a169;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  padding: 0.5rem;
  background-color: #f0fff4;
  border-radius: 4px;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const PartnerProfile = () => {
  const { currentUser, updateUserProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [partnerData, setPartnerData] = useState(null);
  
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    phone: '',
    specialization: '',
    yearsExperience: '',
    qualifications: '',
    bio: '',
    consultationFee: '',
    availableDays: '',
    availableHours: ''
  });
  
  useEffect(() => {
    const fetchPartnerData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Fetch partner profile data
        const partnerRef = doc(db, 'partners', currentUser.uid);
        const partnerDoc = await getDoc(partnerRef);
        
        if (partnerDoc.exists()) {
          const data = partnerDoc.data();
          setPartnerData(data);
          
          setFormData({
            displayName: data.displayName || '',
            email: data.email || '',
            phone: data.phone || '',
            specialization: data.specialization || '',
            yearsExperience: data.yearsExperience || '',
            qualifications: data.qualifications || '',
            bio: data.bio || '',
            consultationFee: data.consultationFee || '',
            availableDays: data.availableDays || '',
            availableHours: data.availableHours || ''
          });
        }
      } catch (err) {
        console.error('Error fetching partner data:', err);
        setError('Failed to load your profile data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPartnerData();
  }, [currentUser]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      // Update profile in Firebase Auth and Firestore
      const profileData = {
        displayName: formData.displayName,
        phone: formData.phone,
        specialization: formData.specialization,
        yearsExperience: formData.yearsExperience,
        qualifications: formData.qualifications,
        bio: formData.bio,
        consultationFee: formData.consultationFee,
        availableDays: formData.availableDays,
        availableHours: formData.availableHours,
        updatedAt: new Date()
      };
      
      // Update Auth profile
      await updateUserProfile(currentUser, {
        displayName: formData.displayName
      });
      
      // Update Firestore document
      const partnerRef = doc(db, 'partners', currentUser.uid);
      await setDoc(partnerRef, profileData, { merge: true });
      
      setSuccess('Profile updated successfully!');
      
      // Update local state
      setPartnerData(prev => ({
        ...prev,
        ...profileData
      }));
      
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && !partnerData) {
    return (
      <PageContainer>
        <Title>Partner Profile</Title>
        <Subtitle>Loading your profile data...</Subtitle>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <Title>Partner Profile</Title>
      <Subtitle>Manage your professional information and settings</Subtitle>
      
      <ProfileGrid>
        <ProfileImageSection>
          <ProfileImage>
            {currentUser?.photoURL ? (
              <img src={currentUser.photoURL} alt="Profile" />
            ) : (
              <FaUser />
            )}
          </ProfileImage>
          <Button variant="outline">Change Photo</Button>
        </ProfileImageSection>
        
        <div>
          <Card>
            <Card.Header>Professional Information</Card.Header>
            <Card.Content>
              <form onSubmit={handleSubmit}>
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="displayName">Full Name</Label>
                    <Input
                      type="text"
                      id="displayName"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleChange}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled
                    />
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="specialization">Specialization</Label>
                    <Select
                      id="specialization"
                      name="specialization"
                      value={formData.specialization}
                      onChange={handleChange}
                    >
                      <option value="">Select your specialization</option>
                      <option value="clinical">Clinical Nutrition</option>
                      <option value="sports">Sports Nutrition</option>
                      <option value="weight">Weight Management</option>
                      <option value="pediatric">Pediatric Nutrition</option>
                      <option value="plant-based">Plant-based Nutrition</option>
                      <option value="other">Other</option>
                    </Select>
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="yearsExperience">Years of Experience</Label>
                    <Input
                      type="number"
                      id="yearsExperience"
                      name="yearsExperience"
                      value={formData.yearsExperience}
                      onChange={handleChange}
                      min="0"
                      max="50"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="consultationFee">Consultation Fee (USD)</Label>
                    <Input
                      type="number"
                      id="consultationFee"
                      name="consultationFee"
                      value={formData.consultationFee}
                      onChange={handleChange}
                      min="0"
                    />
                  </FormGroup>
                </FormRow>
                
                <SectionTitle>Qualifications & Bio</SectionTitle>
                
                <FormGroup>
                  <Label htmlFor="qualifications">Qualifications</Label>
                  <Textarea
                    id="qualifications"
                    name="qualifications"
                    value={formData.qualifications}
                    onChange={handleChange}
                    placeholder="List your degrees, certifications, and other qualifications"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="bio">Professional Bio</Label>
                  <Textarea
                    id="bio"
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    placeholder="Tell potential clients about your background, approach, and expertise"
                  />
                </FormGroup>
                
                <SectionTitle>Availability</SectionTitle>
                
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="availableDays">Available Days</Label>
                    <Input
                      type="text"
                      id="availableDays"
                      name="availableDays"
                      value={formData.availableDays}
                      onChange={handleChange}
                      placeholder="e.g., Monday-Friday"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="availableHours">Available Hours</Label>
                    <Input
                      type="text"
                      id="availableHours"
                      name="availableHours"
                      value={formData.availableHours}
                      onChange={handleChange}
                      placeholder="e.g., 9:00 AM - 5:00 PM"
                    />
                  </FormGroup>
                </FormRow>
                
                {error && <ErrorMessage>{error}</ErrorMessage>}
                {success && <SuccessMessage>{success}</SuccessMessage>}
                
                <Button type="submit" disabled={loading} style={{ marginTop: '1.5rem' }}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </Card.Content>
          </Card>
        </div>
      </ProfileGrid>
    </PageContainer>
  );
};

export default PartnerProfile; 